
import "./global.css";

import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "./components/Layout";

import { UserProvider, useUser } from "./contexts/UserContext";
import { TaxProvider } from "./contexts/TaxContext";
import { PaymentMethodProvider } from "./contexts/PaymentMethodContext";

import Index from "./pages/Index";
import Clientes from "./pages/Clientes";
import ClienteForm from "./pages/ClienteForm";
import Productos from "./pages/Productos";
import ProductoForm from "./pages/ProductoForm";
import Facturas from "./pages/Facturas";
import FacturaForm from "./pages/FacturaForm";
import Presupuestos from "./pages/Presupuestos";
import PresupuestoForm from "./pages/PresupuestoForm";
import Proformas from "./pages/Proformas";
import ProformaForm from "./pages/ProformaForm";
import Rectificativas from "./pages/Rectificativas";
import RectificativaForm from "./pages/RectificativaForm";
import Gastos from "./pages/Gastos";
import GastoForm from "./pages/GastoForm";
import Configuracion from "./pages/Configuracion";
import Certificados from "./pages/Certificados";
import Verifactu from "./pages/Verifactu";
import Face from "./pages/Face";
import Notas from "./pages/Notas";
import Tareas from "./pages/Tareas";
import Usuarios from "./pages/Usuarios";
import MiPerfil from "./pages/MiPerfil";
import Login from "./pages/Login";
import VerificacionPublica from "./pages/VerificarFactura";


const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <UserProvider>
    <TaxProvider>
      <PaymentMethodProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          {children}
        </TooltipProvider>
      </PaymentMethodProvider>
    </TaxProvider>
  </UserProvider>
);

const AppLayout = () => (
  <Layout>
    <Outlet />
  </Layout>
);

const ProtectedRoute = () => {
  const { currentUser, isLoading } = useUser();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p>Cargando sesión...</p>
      </div>
    );
  }

  return currentUser ? <AppLayout /> : <Navigate to="/login" replace />;
};


const AppRoutes = () => {
  const { currentUser, isLoading } = useUser();

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/login" element={<Login />} />

        {/* Ruta pública de verificación Verifactu - NO requiere autenticación */}
        <Route path="/verificar/:userId/:numeroRegistro/:hash" element={<VerificacionPublica />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<Index />} />
          <Route path="/clientes" element={<Clientes />} />
          <Route path="/clientes/new" element={<ClienteForm />} />
          <Route path="/clientes/:id/edit" element={<ClienteForm />} />
          <Route path="/productos" element={<Productos />} />
          <Route path="/productos/nuevo" element={<ProductoForm />} />
          <Route path="/productos/:id/edit" element={<ProductoForm />} />

          <Route path="/facturas" element={<Facturas />} />
          <Route path="/facturas/nueva" element={<FacturaForm />} />
          <Route path="/facturas/:id/edit" element={<FacturaForm />} />

          <Route path="/presupuestos" element={<Presupuestos />} />
          <Route path="/presupuestos/nuevo" element={<PresupuestoForm />} />
          <Route path="/presupuestos/:id/edit" element={<PresupuestoForm />} />

          <Route path="/proformas" element={<Proformas />} />
          <Route path="/proformas/nuevo" element={<ProformaForm />} />
          <Route path="/proformas/:id/edit" element={<ProformaForm />} />

          <Route path="/rectificativas" element={<Rectificativas />} />
          <Route path="/rectificativas/nueva" element={<RectificativaForm />} />
          <Route path="/rectificativas/:id/edit" element={<RectificativaForm />} />

          {/* Rutas para la gestión de Gastos */}
          <Route path="/gastos" element={<Gastos />} />
          <Route path="/gastos/nuevo" element={<GastoForm />} />
          <Route path="/gastos/:id/edit" element={<GastoForm />} />

          <Route path="/notas" element={<Notas />} />
          <Route path="/tareas" element={<Tareas />} />
          <Route path="/usuarios" element={<Usuarios />} />
          <Route path="/perfil" element={<MiPerfil />} />
          <Route path="/configuracion" element={<Configuracion />} />
          <Route path="/certificados" element={<Certificados />} />
          <Route path="/verifactu" element={<Verifactu />} />
          <Route path="/face" element={<Face />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

const App = () => (
  <AppProviders>
    <AppRoutes />
  </AppProviders>
);

const container = document.getElementById("root")!;
const root = createRoot(container);
root.render(<App />);
